import * as React from 'react';
import { useSelector } from 'react-redux';

import { useChatMessageMenu } from '../../ChatMessageMenuContainer';
import { useDesktopContextMenu, useMobileContextMenu } from '../../../hooks/useContextMenu';
import { currentOpenChatIDSelector } from '../../../selectors';
import { useApplicationContext } from '../../../utils/applicationContext';
import { EDeviceType } from '../../../types/deviceType';

type TUseMessageContextMenuParams = {
  messageId: string;
  isDeleted: boolean | undefined;
  messageRef: React.RefObject<HTMLDivElement | null>;
};

export const useMessageContextMenu = ({ messageId, isDeleted = false, messageRef }: TUseMessageContextMenuParams) => {
  const {
    custom: { realDevice },
  } = useApplicationContext();

  const isDesktop = realDevice === EDeviceType.Desktop;

  const chatId = useSelector(currentOpenChatIDSelector) || '';

  const { showChatMessageMenu, hideChatMessageMenu } = useChatMessageMenu();

  const handleShowDesktopContextMenu = React.useCallback(
    (event: MouseEvent): boolean => {
      event.preventDefault();

      const position = {
        x: event.clientX,
        y: event.clientY,
      };

      showChatMessageMenu(chatId, messageId, position);

      return true;
    },
    [showChatMessageMenu, chatId, messageId],
  );

  const handleShowMobileContextMenu = React.useCallback(() => {
    showChatMessageMenu(chatId, messageId);
  }, [showChatMessageMenu, chatId, messageId]);

  useDesktopContextMenu({
    elementRef: messageRef,
    isMenuEnabled: Boolean(isDesktop && !isDeleted),
    onShowContextMenu: handleShowDesktopContextMenu,
    onHideContextMenu: hideChatMessageMenu,
  });

  const { cancelLongPress } = useMobileContextMenu({
    elementRef: messageRef,
    isMenuEnabled: Boolean(!isDesktop && !isDeleted),
    onShowContextMenu: handleShowMobileContextMenu,
  });

  return { cancelLongPress };
};
